// frontend/src/services/tradingService.js
import api, { api_v2 } from './api';

const tradingService = {
  // -----------------------
  // dicts (MUST use api_v2)
  // -----------------------
  dicts: {
    symbols: async ({ signal } = {}) => {
      const response = await api_v2.get('/trading/dicts/symbols', { signal });
      return response.data;
    },

    exchanges: async ({ signal } = {}) => {
      const response = await api_v2.get('/trading/dicts/exchanges', { signal });
      return response.data;
    },

    status: async ({ signal } = {}) => {
      const response = await api_v2.get('/trading/dicts/status', { signal });
      return response.data;
    },

    directions: async ({ signal } = {}) => {
      const response = await api_v2.get('/trading/dicts/directions', { signal });
      return response.data;
    },
  },

  // -----------------------
  // accounts (api v1)
  // -----------------------
  accounts: {
    list: async ({ limit, offset, strategies_only, subscriptions_only, signal } = {}) => {
      const qs = new URLSearchParams();

      if (limit !== undefined) qs.set('limit', String(limit));
      if (offset !== undefined) qs.set('offset', String(offset));
      if (strategies_only !== undefined) qs.set('strategies_only', String(strategies_only));
      if (subscriptions_only !== undefined) qs.set('subscriptions_only', String(subscriptions_only));

      const url = `/trading/account/${qs.toString() ? `?${qs.toString()}` : ''}`;
      const response = await api.get(url, { signal });
      return response.data;
    },

    get: async ({ number, signal }) => {
      if (!number) throw new Error('accounts.get: "number" is required');
      const response = await api.get(`/trading/account/${encodeURIComponent(number)}/`, { signal });
      return response.data;
    },

    create: async ({ label, exchangeId, signal }) => {
      const payload = {
        label,
        exchange: { id: exchangeId },
      };
      const response = await api.post('/trading/account/', payload, { signal });
      return response.data;
    },

    update: async ({ number, label, exchangeId, is_active, signal }) => {
      if (!number) throw new Error('accounts.update: "number" is required');
      const payload = {
        ...(label !== undefined ? { label } : null),
        ...(exchangeId !== undefined ? { exchange: { id: exchangeId } } : null),
        ...(is_active !== undefined ? { is_active } : null),
      };
      const response = await api.post(`/trading/account/${encodeURIComponent(number)}/`, payload, {
        signal,
      });
      return response.data;
    },

    keys: {
      get: async ({ number, signal }) => {
        if (!number) throw new Error('accounts.keys.get: "number" is required');
        const response = await api.get(`/trading/account/${encodeURIComponent(number)}/keys/`, {
          signal,
        });
        return response.data;
      },

      set: async ({ number, api_key, api_secret, api_passphrase, signal }) => {
        if (!number) throw new Error('accounts.keys.set: "number" is required');
        const payload = { api_key, api_secret, api_passphrase };
        const response = await api.post(
          `/trading/account/${encodeURIComponent(number)}/keys/`,
          payload,
          { signal }
        );
        return response.data;
      },
    },

    subscriptions: {
      listForAccount: async ({ number, limit, offset, signal }) => {
        if (!number) throw new Error('accounts.subscriptions.listForAccount: "number" is required');
        const response = await api.get(`/trading/account/${encodeURIComponent(number)}/subscriptions/`, {
          params: {
            ...(limit !== undefined ? { limit } : null),
            ...(offset !== undefined ? { offset } : null),
          },
          signal,
        });
        return response.data;
      },

      createForAccount: async ({ number, strategyShortId, volume_limit, signal }) => {
        if (!number) throw new Error('accounts.subscriptions.createForAccount: "number" is required');
        const payload = {
          trading_strategy: { short_id: strategyShortId },
          volume_limit,
        };
        const response = await api.post(
          `/trading/account/${encodeURIComponent(number)}/subscriptions/create/`,
          payload,
          { signal }
        );
        return response.data;
      },

      updateForAccount: async ({ number, subShortId, volume_limit, is_active, signal }) => {
        if (!number) throw new Error('accounts.subscriptions.updateForAccount: "number" is required');
        if (!subShortId)
          throw new Error('accounts.subscriptions.updateForAccount: "subShortId" is required');

        const payload = {
          ...(volume_limit !== undefined ? { volume_limit } : null),
          ...(is_active !== undefined ? { is_active } : null),
        };

        const response = await api.put(
          `/trading/account/${encodeURIComponent(number)}/subscriptions/${encodeURIComponent(
            subShortId
          )}/update/`,
          payload,
          { signal }
        );
        return response.data;
      },
    },

    // ✅ requested: endpoint exposed under tradingService.accounts.strategy
    strategy: {
      subscribe: async ({ number, strategyShortId, volume_limit, signal } = {}) => {
        if (!number) throw new Error('accounts.strategy.subscribe: "number" is required');
        if (!strategyShortId)
          throw new Error('accounts.strategy.subscribe: "strategyShortId" is required');

        const payload = {
          trading_strategy: { short_id: strategyShortId },
          volume_limit,
        };

        const response = await api.post(
          `/trading/account/${encodeURIComponent(number)}/subscriptions/create/`,
          payload,
          { signal }
        );
        return response.data;
      },
    },

    balances: {
      listForBalances: async ({ number, limit = 10, offset = 0, signal } = {}) => {
        if (!number) throw new Error('accounts.balances.listForAccount: "number" is required');

        const response = await api.get(`/trading/account/${encodeURIComponent(number)}/balances/`, {
          params: { limit, offset },
          signal,
        });
        return response.data;
      },
    },

    performance: {
      history: async ({ number, limit = 365, signal } = {}) => {
        if (!number) throw new Error('accounts.performance.history: "number" is required');

        const response = await api.get(
          `/trading/account/${encodeURIComponent(number)}/performance/`,
          {
            params: { limit },
            signal,
          }
        );
        return response.data;
      },
    },

    trades: {
      listForAccount: async ({ number, limit = 10, offset = 0, signal } = {}) => {
        if (!number) throw new Error('accounts.trades.listForAccount: "number" is required');

        const response = await api.get(`/trading/account/${encodeURIComponent(number)}/trade/`, {
          params: { limit, offset },
          signal,
        });
        return response.data;
      },
      tradeDetails: async ({ number, tradeId, signal } = {}) => {
        if (!number) throw new Error('accounts.trades.tradeDetails: "number" is required');
        if (!tradeId) throw new Error('accounts.trades.tradeDetails: "tradeId" is required');
        const response = await api.get(`/trading/account/${encodeURIComponent(number)}/trade/${encodeURIComponent(tradeId)}/`, { signal });
        return response.data;
      },
      addForAccount: async ({
        number,
        symbol,
        direction,
        quantity,
        quote_quantity,
        comment,
        signal,
      } = {}) => {
        if (!number) throw new Error('accounts.trades.addForAccount: "number" is required');
        if (!symbol) throw new Error('accounts.trades.addForAccount: "symbol" is required');
        if (!direction) throw new Error('accounts.trades.addForAccount: "direction" is required');

        const qty = Number(quantity || 0);
        const quoteQty = Number(quote_quantity || 0);

        if (!(qty > 0) && !(quoteQty > 0)) {
          throw new Error(
            'accounts.trades.addForAccount: one of "quantity" or "quote_quantity" must be > 0'
          );
        }

        const payload = {
          symbol,
          direction,
          ...(qty > 0 ? { quantity: qty } : { quantity: 0 }),
          ...(quoteQty > 0 ? { quote_quantity: quoteQty } : { quote_quantity: 0 }),
          ...(comment ? { comment } : null),
        };

        const response = await api.post(
          `/trading/account/${encodeURIComponent(number)}/trade/`,
          payload,
          { signal }
        );
        return response.data;
      }
    },
    strategies: {
      getStrategiesForAccount: async ({ number, limit, offset, signal } = {}) => {
        if (!number) throw new Error('accounts.strategies.getStrategiesForAccount: "number" is required');

        const response = await api.get('/trading/strategy/', {
          params: {
            ...(limit !== undefined ? { limit } : null),
            ...(offset !== undefined ? { offset } : null),
            account_number: number,
          },
          signal,
        });
        return response.data;
      },
    },
  },

  // -----------------------
  // strategies (api v1)
  // -----------------------
  strategies: {
    list: async ({ limit, offset, all_strategies, signal } = {}) => {
          const response = await api.get('/trading/strategy/', {
            params: {
              ...(limit !== undefined ? { limit } : null),
              ...(offset !== undefined ? { offset } : null),
              ...(all_strategies !== undefined ? { all_strategies } : null),
            },
            signal,
          });
          return response.data;
        },

        get: async ({ strategyId, recentTradesLimit = 10, signal }) => {
          if (!strategyId) throw new Error('strategies.get: "strategyId" is required');
          const response = await api.get(`/trading/strategy/${encodeURIComponent(strategyId)}/`, {
            params: {
              recent_trades_limit: recentTradesLimit,
            },
            signal,
          });
          return response.data;
        },

    upsert: async ({ trading_strategy_id, payload, signal }) => {
      // Postman shows POST /trading/strategy/:trading_strategy_id/ for update,
      // and POST /trading/strategy/ for create.
      if (trading_strategy_id) {
        const response = await api.post(
          `/trading/strategy/${encodeURIComponent(trading_strategy_id)}/`,
          payload,
          { signal }
        );
        return response.data;
      }
      const response = await api.post('/trading/strategy/', payload, { signal });
      return response.data;
    },

    deactivate: async ({ strategyId, signal } = {}) => {
      if (!strategyId) throw new Error('strategies.deactivate: "strategyId" is required');
      const response = await api.delete(`/trading/strategy/${encodeURIComponent(strategyId)}/`, {
        signal,
      });
      return response.data;
    },

    subscriptions: {
      listForStrategy: async ({ tradingStrategyId, limit, offset, signal }) => {
        if (!tradingStrategyId)
          throw new Error('strategies.subscriptions.listForStrategy: "tradingStrategyId" is required');

        const response = await api.get(
          `/trading/strategy/${encodeURIComponent(tradingStrategyId)}/subscriptions/`,
          {
            params: {
              ...(limit !== undefined ? { limit } : null),
              ...(offset !== undefined ? { offset } : null),
            },
            signal,
          }
        );
        return response.data;
      },
    },
  },
};

export default tradingService;
